// Parent Progress tab: Pip Reading Report — unified SLR view of reading
// modes used, independence vs Pip-supported work, comprehension, retells,
// fluency and recommended next practice. Concise + expandable detail.
import { useEffect, useState } from "react";
import { BookOpenCheck } from "lucide-react";
import { api } from "../../lib/api";

const pct = (r) => (r == null ? "not yet" : `${Math.round(r * 100)}%`);

export const ReadingReportCard = () => {
  const [reports, setReports] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/slr/family/reading-report")
      .then(({ data }) => setReports(data.reports || []))
      .catch(() => { setError(true); setReports([]); });
  }, []);
  if (reports === null) {
    return (
      <section className="bg-card rounded-2xl border border-border p-5 animate-pulse h-28"
        data-testid="reading-report-loading" />
    );
  }
  if (error) {
    return (
      <section className="bg-card rounded-2xl border border-border p-5"
        data-testid="reading-report-error">
        <p className="text-xs text-muted-foreground">
          The Pip Reading Report couldn't load — refresh to try again.
        </p>
      </section>
    );
  }
  if (!reports.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5"
      data-testid="reading-report-card">
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-1">
        <BookOpenCheck className="h-5 w-5 text-emerald-600" aria-hidden="true" />
        Pip Reading Report
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        Speech, language and reading — one unified view of practice, understanding and independence.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {reports.map((r) => r.empty ? (
          <div key={r.child_id} className="rounded-xl border border-dashed border-border p-3 self-start"
            data-testid={`reading-report-empty-${r.child_id}`}>
            <p className="text-sm font-bold">{r.child_name}</p>
            <p className="text-xs text-muted-foreground">
              No reading activity yet — Pip's Library is a lovely first stop!
            </p>
          </div>
        ) : (
          <div key={r.child_id} className="rounded-xl border border-border p-3 space-y-2"
            data-testid={`reading-report-${r.child_id}`}>
            <div className="flex justify-between gap-2 text-sm flex-wrap">
              <span className="font-bold">{r.child_name}</span>
              <span className="text-xs text-muted-foreground"
                data-testid={`reading-report-summary-${r.child_id}`}>{r.summary}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.modes.slice(0, 6).map((m) => (
                <span key={m.mode}
                  className="text-[11px] rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 px-2.5 py-0.5"
                  data-testid={`reading-report-mode-${r.child_id}-${m.mode}`}>
                  {m.label} ×{m.count}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.strengths.map((t, i) => (
                <span key={t} className="text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-900 px-2.5 py-0.5"
                  data-testid={`reading-report-strength-${r.child_id}-${i}`}>
                  Strength: {t}
                </span>
              ))}
              {r.reinforce.map((t, i) => (
                <span key={t} className="text-[11px] font-bold rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5"
                  data-testid={`reading-report-reinforce-${r.child_id}-${i}`}>
                  Reinforce: {t}
                </span>
              ))}
            </div>
            {r.recommendations.length > 0 && (
              <ul className="space-y-1" data-testid={`reading-report-recs-${r.child_id}`}>
                {r.recommendations.map((rec, i) => (
                  <li key={i} className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">Next: {rec.title}</span> — {rec.why}
                  </li>
                ))}
              </ul>
            )}
            <button type="button"
              onClick={() => setOpen(open === r.child_id ? null : r.child_id)}
              className="text-xs font-bold text-primary underline underline-offset-2"
              data-testid={`reading-report-toggle-${r.child_id}`}>
              {open === r.child_id ? "Hide details" : "View details"}
            </button>
            {open === r.child_id && (
              <div className="space-y-2 text-xs text-muted-foreground"
                data-testid={`reading-report-detail-${r.child_id}`}>
                <p>
                  <span className="font-semibold text-foreground">Independence:</span>{" "}
                  {r.independence.independent} independent · {r.independence.supported} with Pip's help
                  {r.independence.solo_accuracy != null &&
                    ` · solo reading accuracy ${pct(r.independence.solo_accuracy)}`}
                  {r.independence.with_pip_accuracy != null &&
                    ` · with Pip ${pct(r.independence.with_pip_accuracy)}`}
                </p>
                <p>
                  <span className="font-semibold text-foreground">Comprehension:</span>{" "}
                  {r.comprehension.attempts === 0 && r.comprehension.quizzes_done === 0
                    ? "no comprehension checks yet — try Understand It on a library book"
                    : <>{pct(r.comprehension.ratio)} correct · {r.comprehension.quizzes_done} book quiz{r.comprehension.quizzes_done === 1 ? "" : "zes"} done · first-try {pct(r.comprehension.first_try_ratio)}</>}
                </p>
                <p>
                  <span className="font-semibold text-foreground">Tell It Back:</span>{" "}
                  {r.retell.total === 0
                    ? "no retells yet — Tell It Back shows real understanding"
                    : `${r.retell.good}/${r.retell.total} retells showed understanding`}
                </p>
                <p>
                  <span className="font-semibold text-foreground">Fluency:</span>{" "}
                  {r.fluency.pages_read === 0
                    ? "no read-aloud pages yet — try Read Myself in Pip's Library"
                    : <>{r.fluency.pages_read} pages read aloud · accuracy {pct(r.fluency.accuracy)}{r.fluency.trend ? ` · trending ${r.fluency.trend}` : ""}</>}
                </p>
                {r.skills.length > 0 && (
                  <ul className="space-y-0.5">
                    {r.skills.map((s) => (
                      <li key={s.skill_id} className="flex justify-between gap-2">
                        <span><span className="font-semibold text-foreground">{s.title}</span> · {s.level}</span>
                        <span className="shrink-0">{s.attempts} tries · {pct(s.ratio)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
